/**
 * constants.ts - Application Constants and Mock Data
 * 
 * This file contains all static data and configuration constants
 * used throughout the application. This makes it easy to modify
 * mock data and ensures consistency across components.
 */

// Type Definitions
export interface SectionPosition {
  x: number;
  y: number;
  angle: number;
}

export interface Modifier {
  name: string;
  type: 'required' | 'optional';
  minSelections: number;
  maxSelections: number;
  options: ModifierOption[];
}

export interface ModifierOption {
  name: string;
  priceAdjustment: number;
  isDefault: boolean;
}

export interface FoodMenuItem {
  id: string;
  name: string;
  price: number;
  image: string | null;
  description: string;
  category?: string;
  available?: boolean;
  allergens?: string[];
  dietaryTags?: string[];
  modifiers?: Modifier[];
  is_promo?: boolean;
  promo_price?: number | null;
  featured?: boolean;
}

export interface Vendor {
  id: string;
  name: string;
  sections: string[];
  cuisine_type?: string | null;
  description?: string | null;
  status?: 'active' | 'inactive';
  currentWaitMinutes?: number | null;
  revenue: string | number;
  image?: string | null;
  menuItems: FoodMenuItem[];
}

export interface DashboardData {
  restaurant: {
    orders: number;
    avgWaitTime: string;
    popularItems: string[];
    revenue: string;
  };
  super: {
    totalOrders: number;
    avgWaitTime: string;
    topRestaurants: string[];
    totalRevenue: string;
  };
}

export interface PermissionLevel {
  value: string;
  label: string;
  description: string;
}

export interface StatusOption {
  value: string;
  label: string;
}

export interface StadiumInfo {
  name: string;
  city: string;
  state: string;
  capacity: number;
  opened: number;
  teams: string[];
}

export interface ColorPalette {
  primary: string;
  primaryHover: string;
  secondary: string;
  success: string;
  danger: string;
  warning: string;
  light: string;
  white: string;
  gray100: string;
  gray200: string;
  gray300: string;
  gray400: string;
  gray500: string;
  gray600: string;
  gray700: string;
  gray800: string;
  gray900: string;
  falcons: {
    red: string;
    black: string;
    silver: string;
    white: string;
  };
}

export interface NavigationMenuItem {
  id: string;
  label: string;
  icon: string;
}

// Mercedes-Benz Stadium sections configuration
export const MERCEDES_BENZ_SECTIONS: string[] = [
  // Lower Level (100s)
  '101', '102', '103', '104', '105', '106', '107', '108', '109', '110', '111',
  '112', '113', '114', '115', '116', '117', '118', '119', '120', '121', '122', '123', '124',
  '125', '126', '127', '128', '129', '130', '131', '132', '133', '134', '135',
  '136', '137', '138', '139', '140', '141', '142', '143', '144', '145', '146', '147', '148',
  
  // Club Level (200s)
  '201', '202', '203', '204', '205', '206', '207', '208', '209', '210', '211',
  '212', '213', '214', '215', '216', '217', '218', '219', '220', '221', '222',
  '223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233',
  '234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244',
  
  // Upper Level (300s)
  '301', '302', '303', '304', '305', '306', '307', '308', '309', '310', '311',
  '312', '313', '314', '315', '316', '317', '318', '319', '320', '321', '322', '323', '324',
  '325', '326', '327', '328', '329', '330', '331', '332', '333', '334', '335',
  '336', '337', '338', '339', '340', '341', '342', '343', '344', '345', '346', '347', '348',
  
  // Premium Suites
  'S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10'
];

// Legacy stadium sections (kept for backward compatibility)
export const STADIUM_SECTIONS: string[] = [
  'A1', 'A2', 'A3', 'A4', 'A5',
  'B1', 'B2', 'B3', 'B4', 'B5',
  'C1', 'C2', 'C3', 'C4', 'C5',
  'D1', 'D2', 'D3', 'D4', 'D5'
];

// Mercedes-Benz Stadium section groups for easy reference
export const MERCEDES_BENZ_SECTION_GROUPS: Record<string, string[]> = {
  // Lower Level
  lowerNorth: ['101', '102', '103', '104', '105', '106', '107', '108', '109', '110', '111'],
  lowerEast: ['112', '113', '114', '115', '116', '117', '118', '119', '120', '121', '122', '123', '124'],
  lowerSouth: ['125', '126', '127', '128', '129', '130', '131', '132', '133', '134', '135'],
  lowerWest: ['136', '137', '138', '139', '140', '141', '142', '143', '144', '145', '146', '147', '148'],
  
  // Club Level
  clubNorth: ['201', '202', '203', '204', '205', '206', '207', '208', '209', '210', '211'],
  clubEast: ['212', '213', '214', '215', '216', '217', '218', '219', '220', '221', '222'],
  clubSouth: ['223', '224', '225', '226', '227', '228', '229', '230', '231', '232', '233'],
  clubWest: ['234', '235', '236', '237', '238', '239', '240', '241', '242', '243', '244'],
  
  // Upper Level
  upperNorth: ['301', '302', '303', '304', '305', '306', '307', '308', '309', '310', '311'],
  upperEast: ['312', '313', '314', '315', '316', '317', '318', '319', '320', '321', '322', '323', '324'],
  upperSouth: ['325', '326', '327', '328', '329', '330', '331', '332', '333', '334', '335'],
  upperWest: ['336', '337', '338', '339', '340', '341', '342', '343', '344', '345', '346', '347', '348'],
  
  // Premium Suites
  suites: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8', 'S9', 'S10']
};

// Section positions for legacy SVG map
export const SECTION_POSITIONS: Record<string, SectionPosition> = {
  // Top sections (A)
  A1: { x: 130, y: 80, angle: -60 },
  A2: { x: 160, y: 80, angle: -30 },
  A3: { x: 200, y: 80, angle: 0 },
  A4: { x: 240, y: 80, angle: 30 },
  A5: { x: 270, y: 80, angle: 60 },
  
  // Left sections (B)
  B1: { x: 80, y: 110, angle: 140 },
  B2: { x: 60, y: 130, angle: 160 },
  B3: { x: 50, y: 150, angle: 180 },
  B4: { x: 60, y: 170, angle: 200 },
  B5: { x: 80, y: 190, angle: 220 },
  
  // Right sections (C)
  C1: { x: 320, y: 110, angle: 40 },
  C2: { x: 340, y: 130, angle: 20 },
  C3: { x: 350, y: 150, angle: 0 },
  C4: { x: 340, y: 170, angle: -20 },
  C5: { x: 320, y: 190, angle: -40 },
  
  // Bottom sections (D)
  D1: { x: 130, y: 220, angle: -120 },
  D2: { x: 160, y: 220, angle: -150 },
  D3: { x: 200, y: 220, angle: 180 },
  D4: { x: 240, y: 220, angle: 150 },
  D5: { x: 270, y: 220, angle: 120 }
};

// Mock dashboard data
export const MOCK_DASHBOARD_DATA: DashboardData = {
  restaurant: {
    orders: 45,
    avgWaitTime: '12 min',
    popularItems: ['Burger', 'Fries', 'Cola'],
    revenue: '$1,250'
  },
  super: {
    totalOrders: 342,
    avgWaitTime: '15 min',
    topRestaurants: [],
    totalRevenue: '$12,450'
  }
};

// Section level mapping for pricing/features
export const SECTION_LEVELS: Record<string, string[]> = {
  lower: MERCEDES_BENZ_SECTION_GROUPS.lowerNorth
    .concat(MERCEDES_BENZ_SECTION_GROUPS.lowerEast)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.lowerSouth)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.lowerWest),
  club: MERCEDES_BENZ_SECTION_GROUPS.clubNorth
    .concat(MERCEDES_BENZ_SECTION_GROUPS.clubEast)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.clubSouth)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.clubWest),
  upper: MERCEDES_BENZ_SECTION_GROUPS.upperNorth
    .concat(MERCEDES_BENZ_SECTION_GROUPS.upperEast)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.upperSouth)
    .concat(MERCEDES_BENZ_SECTION_GROUPS.upperWest),
  suites: MERCEDES_BENZ_SECTION_GROUPS.suites
};

// UI Constants — aligned with CSS tokens (--brand-primary)
export const COLORS: ColorPalette = {
  primary: '#FF6B35',
  primaryHover: '#E55A2B',
  secondary: '#6b7280',
  success: '#059669',
  danger: '#dc2626',
  warning: '#d97706',
  light: '#f8fafc',
  white: '#ffffff',
  gray100: '#f3f4f6',
  gray200: '#e5e7eb',
  gray300: '#d1d5db',
  gray400: '#9ca3af',
  gray500: '#6b7280',
  gray600: '#4b5563',
  gray700: '#374151',
  gray800: '#1f2937',
  gray900: '#111827',
  falcons: {
    red: '#A71930',
    black: '#000000',
    silver: '#A5ACAF',
    white: '#FFFFFF'
  }
};

export const SPACING: Record<string, string> = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px'
};

export const BORDER_RADIUS: Record<string, string> = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px'
};

// File upload constants
export const ALLOWED_IMAGE_TYPES: string[] = ['image/webp'];
export const MAX_FILE_SIZE: number = 5 * 1024 * 1024; // 5MB

// Default menu item image — SVG placeholder (display only, never stored in DB)
const _DEFAULT_MENU_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">',
  '<defs>',
  '<linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#dc2626"/>',
  '<stop offset="100%" stop-color="#7f1d1d"/>',
  '</linearGradient>',
  '</defs>',
  '<rect width="400" height="300" fill="url(#bg)"/>',
  '<circle cx="200" cy="132" r="72" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="2"/>',
  '<circle cx="200" cy="132" r="56" fill="rgba(255,255,255,0.08)"/>',
  '<rect x="178" y="90" width="3" height="15" rx="1.5" fill="rgba(255,255,255,0.9)"/>',
  '<rect x="184" y="90" width="3" height="15" rx="1.5" fill="rgba(255,255,255,0.9)"/>',
  '<rect x="190" y="90" width="3" height="15" rx="1.5" fill="rgba(255,255,255,0.9)"/>',
  '<path d="M177 105 Q177 116 181 118 L181 170 Q181 174 184.5 174 Q188 174 188 170 L188 118 Q192 116 192 105 Z" fill="rgba(255,255,255,0.9)"/>',
  '<path d="M214 90 Q224 90 224 108 L224 120 L214 120 Z" fill="rgba(255,255,255,0.9)"/>',
  '<rect x="214" y="120" width="8" height="54" rx="4" fill="rgba(255,255,255,0.9)"/>',
  '<text x="200" y="236" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="13" fill="rgba(255,255,255,0.65)" font-weight="500" letter-spacing="3">NO IMAGE</text>',
  '</svg>',
].join('');
export const DEFAULT_ITEM_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(_DEFAULT_MENU_SVG);

// Default event/promotion image — megaphone SVG placeholder (landscape 2:1)
const _DEFAULT_EVENT_SVG = [
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 400 200">',
  '<defs>',
  '<linearGradient id="evbg" x1="0" y1="0" x2="1" y2="1">',
  '<stop offset="0%" stop-color="#dc2626"/>',
  '<stop offset="100%" stop-color="#7f1d1d"/>',
  '</linearGradient>',
  '</defs>',
  '<rect width="400" height="200" fill="url(#evbg)"/>',
  '<circle cx="200" cy="95" r="68" fill="none" stroke="rgba(255,255,255,0.10)" stroke-width="1.5"/>',
  '<circle cx="200" cy="95" r="52" fill="rgba(255,255,255,0.06)"/>',
  '<rect x="108" y="74" width="24" height="32" rx="3" fill="rgba(255,255,255,0.9)"/>',
  '<polygon points="132,78 238,56 238,134 132,106" fill="rgba(255,255,255,0.9)"/>',
  '<rect x="116" y="106" width="14" height="26" rx="7" fill="rgba(255,255,255,0.9)"/>',
  '<path d="M248,82 Q260,95 248,108" fill="none" stroke="rgba(255,255,255,0.9)" stroke-width="5.5" stroke-linecap="round"/>',
  '<path d="M262,70 Q280,95 262,120" fill="none" stroke="rgba(255,255,255,0.70)" stroke-width="4.5" stroke-linecap="round"/>',
  '<path d="M276,58 Q300,95 276,132" fill="none" stroke="rgba(255,255,255,0.45)" stroke-width="3.5" stroke-linecap="round"/>',
  '<text x="200" y="177" text-anchor="middle" font-family="system-ui,-apple-system,sans-serif" font-size="11" fill="rgba(255,255,255,0.55)" font-weight="500" letter-spacing="3">NO IMAGE</text>',
  '</svg>',
].join('');
export const DEFAULT_EVENT_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(_DEFAULT_EVENT_SVG);

// Navigation menu items - Updated with Users tab
export const MENU_ITEMS: NavigationMenuItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard'
  },
  {
    id: 'vendors',
    label: 'Vendors',
    icon: 'vendors'
  },
  {
    id: 'users',
    label: 'Users',
    icon: 'users'
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: 'orders'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: 'analytics'
  }
];

// User management constants
export const USER_PERMISSION_LEVELS: PermissionLevel[] = [
  { 
    value: 'read-only', 
    label: 'Read Only', 
    description: 'Can view data only' 
  },
  { 
    value: 'editor', 
    label: 'Editor', 
    description: 'Can view and edit data' 
  },
  { 
    value: 'admin', 
    label: 'Admin', 
    description: 'Full access to all features' 
  },
  { 
    value: 'super-admin', 
    label: 'Super Admin', 
    description: 'System administrator' 
  }
];

export const USER_STATUS_OPTIONS: StatusOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' }
];

// Stadium information
export const STADIUM_INFO: StadiumInfo = {
  name: 'Mercedes-Benz Stadium',
  city: 'Atlanta',
  state: 'GA',
  capacity: 71000,
  opened: 2017,
  teams: ['Atlanta Falcons', 'Atlanta United FC']
};